import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { STATUS } from "@/components/AppointmentTable";
import { toast } from "sonner";
import { Search, Phone, ChevronDown, ChevronUp, Eye, FolderOpen, FileImage, Paperclip } from "lucide-react";

function VisitList({ phoneKey, fmtDate, onReceipt }) {
  const [visits, setVisits] = useState(null);
  useEffect(() => {
    api.get(`/patients/${phoneKey}/visits`).then((r) => setVisits(r.data)).catch((e) => toast.error(apiError(e)));
  }, [phoneKey]);
  if (!visits) return <div className="p-4 text-sm text-slate-400">Loading visits…</div>;
  return (
    <div className="divide-y border-t border-slate-100 bg-slate-50/60" data-testid="patient-visits">
      {visits.map((a) => (
        <div key={a.id} className="px-5 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm" data-testid="patient-visit-row">
          <span className="font-mono text-xs text-teal-700 w-20">{a.receipt_no}</span>
          <span className="text-slate-700 w-44">{fmtDate(a.appointment_date)}</span>
          <span className="text-slate-900 font-medium flex-1 min-w-[140px]">{a.doctor_name}</span>
          <span className="text-slate-600 flex-1 min-w-[120px]">{a.disease_name || "—"}</span>
          <span className="font-mono">{formatINR(a.amount)}</span>
          <Badge variant="outline" className={`capitalize ${STATUS[a.status] || ""}`}>{a.status}</Badge>
          <span className="flex items-center gap-2 text-slate-400">
            {a.prescription_path && <FileImage className="h-4 w-4" title="Prescription attached" />}
            {a.attachments?.length > 0 && <span className="flex items-center text-xs"><Paperclip className="h-3.5 w-3.5" />{a.attachments.length}</span>}
          </span>
          <span className="flex gap-1 ml-auto">
            <Button size="icon" variant="ghost" onClick={() => onReceipt(a)} data-testid="visit-receipt-button" title="Receipt"><Eye className="h-4 w-4" /></Button>
            <Link to={`/appointments/${a.id}`} data-testid="visit-detail-link" title="Full record"><Button size="icon" variant="ghost"><FolderOpen className="h-4 w-4" /></Button></Link>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function PatientHistory({ fmtDate, onReceipt }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let stale = false;
    const t = setTimeout(() => {
      api.get("/patients", { params: q ? { q } : {} }).then((r) => { if (!stale) setRows(r.data); }).catch((e) => toast.error(apiError(e)));
    }, 250);
    return () => { stale = true; clearTimeout(t); };
  }, [q]);

  return (
    <div className="space-y-4" data-testid="patient-history">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="patient-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by patient name or phone…" className="pl-9 bg-white" />
        </div>
        <p className="text-sm text-slate-500">Visits are grouped by phone number. Add a phone to appointments to build each patient's history.</p>
      </div>

      {rows === null ? <div className="text-slate-400 text-sm">Loading…</div> : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400" data-testid="patients-empty">No patients with a phone number yet.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y overflow-hidden">
          {rows.map((p) => {
            const expanded = open === p.phone_key;
            return (
              <div key={p.phone_key} data-testid="patient-group">
                <button className="w-full text-left px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-2 hover:bg-slate-50" onClick={() => setOpen(expanded ? null : p.phone_key)} data-testid="patient-group-toggle">
                  <div className="min-w-[180px]">
                    <div className="font-semibold text-slate-900" data-testid="patient-group-name">{p.patient_name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}{p.patient_age != null && <span> · {p.patient_age} yrs</span>}</div>
                  </div>
                  <div className="text-sm text-slate-600"><span className="font-semibold text-slate-900" data-testid="patient-group-visits">{p.visits}</span> visit{p.visits === 1 ? "" : "s"}</div>
                  <div className="text-sm text-slate-600">Total paid <span className="font-mono font-semibold text-teal-700">{formatINR(p.total_paid)}</span></div>
                  <div className="text-sm text-slate-600">Last visit {fmtDate(p.last_visit)}</div>
                  <div className="text-xs text-slate-500 truncate flex-1 min-w-[120px]">{p.doctors.join(", ")}</div>
                  {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 ml-auto" /> : <ChevronDown className="h-4 w-4 text-slate-400 ml-auto" />}
                </button>
                {expanded && <VisitList phoneKey={p.phone_key} fmtDate={fmtDate} onReceipt={onReceipt} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
